
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  getClassificationScores,
  getNormalScores,
  getPrinciplesScores,
  getBalancedEquationScores,
} from '../api';

import { navigation } from '../data';
import { tableHeaders, gameTypes } from './data';
import SubPagesLayout from '../../UI/layout/subPages';

import Table from '../../UI/table';

export default function Scores() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [overallAverage, setOverallAverage] = useState(null);

  const { data: games } = useQuery(gamesQuery()) || {};
  
  const { data: classificationScores } = useQuery({
    queryKey: ['classificationScoresOverall'],
    queryFn: async () => getClassificationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: normalScores } = useQuery({
    queryKey: ['normalScoresOverall'],
    queryFn: async () => getNormalScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: balancedEquationScores } = useQuery({
    queryKey: ['equationScoresOverall'],
    queryFn: async () => getBalancedEquationScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: principlesScores } = useQuery({
    queryKey: ['principlesScoresOverall'],
    queryFn: async () => getPrinciplesScores({completed: true}),
    refetchOnWindowFocus: false,
  });

  const calculateAverages = (scores) => {
    if (!scores || scores.length === 0) return {};

    const grouped = scores.reduce((acc, score) => {
      const key = `${score.game}-${score.attempt}`;
      if (!acc[key]) {
        acc[key] = {
          game: score.game,
          attempt: score.attempt,
          accuracies: [],
        };
      }
      acc[key].accuracies.push(score.total_accuracy);
      return acc;
    }, {});

    const averages = {};
    Object.values(grouped).forEach(({ game, attempt, accuracies }) => {
      const key = `${game}-${attempt}`;
      const sum = accuracies.reduce((a, b) => a + b, 0);
      averages[key] = sum / accuracies.length;
    });

    return averages;
  };

  const findHighestAttempts = (averages) => {
    const gameHighest = {};
    
    Object.entries(averages).forEach(([key, accuracy]) => {
      const [gameId] = key.split('-');
      
      if (!gameHighest[gameId] || accuracy > gameHighest[gameId].accuracy) {
        gameHighest[gameId] = { accuracy, key };
      }
    });
    
    return gameHighest;
  };

  const calculateOverallAverage = (highestAttempts) => {
    const highestScores = Object.values(highestAttempts).map(item => item.accuracy);
    
    if (highestScores.length === 0) return null;
    
    const sum = highestScores.reduce((a, b) => a + b, 0);
    const average = sum / highestScores.length;
    
    return (average * 100).toFixed(2);
  };

  useEffect(() => {
    if (games && classificationScores && normalScores && balancedEquationScores && principlesScores) {
      const allScores = [
        ...(classificationScores || []),
        ...(normalScores || []),
        ...(balancedEquationScores || []),
        ...(principlesScores || [])
      ];

      const averages = calculateAverages(allScores);
      const highestAttempts = findHighestAttempts(averages);

      const overall = calculateOverallAverage(highestAttempts);
      setOverallAverage(overall);

      const maxAttempts = Math.max(
        ...Object.keys(averages).map(key => parseInt(key.split('-')[1])),
        3
      );

      const data = games.map(game => {
        const cells = [];
        
        cells.push({
          value: `${game.name} (${gameTypes[game.game_type]})`,
          style: {}
        });
        
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const key = `${game.id}-${attempt}`;
          const accuracy = averages[key];
          const isHighest = highestAttempts[game.id]?.key === key;
          
          cells.push({
            value: accuracy !== undefined ? `${(accuracy * 100).toFixed(2)}%` : '--',
            style: isHighest ? { backgroundColor: '#EAF3EC' } : {}
          });
        }
        
        return { cells };
      });

      setTableData(data);
    }
  }, [games, classificationScores, normalScores, balancedEquationScores, principlesScores]);
  
  const handlePrevButtonClick = () => {
    navigate('/insights/points-scores/points' );
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      currentPage={1}
      pagesQuantity={1}
      contentClassName="decision-insights-content-container"
    >
      <h1>Points & scores</h1>
      <h2>Accuracy Scores</h2>
      <p>Your overall average score is <b>{overallAverage !== null ? `${overallAverage}%` : '--'}</b>. This is calculated by averaging the highest scores (highlighted in green) from each completed assignment.</p>
      
      <Table headers={tableHeaders} rows={tableData} />
    </SubPagesLayout>
  );
}
